// vendor_editor_view.js — pure HTML-generating module for the full-space
// vendor editor in Settings → Vendors. No DOM access at import time so it
// can be tested under Node.js with `node --test`.

/**
 * Escape HTML special characters to prevent XSS.
 * @param {*} v - value to escape
 * @returns {string} HTML-safe string
 */
export function esc(v) {
  return String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * Service types available in the vendor editor — same categories as the
 * goal editor uses for consistency.
 */
export const SERVICE_TYPES = {
  adaptive: "Adaptive Skills",
  socialrec: "Social Rec",
  nonmedical: "Non-Medical",
  educational: "Educational",
  camp: "Camp",
  therapy: "Clinical Therapies",
  parenttraining: "Parent Training",
  living: "Daily Living & Family",
  coordination: "Coordination",
  access: "Access",
  employment: "Employment & Day",
  residential: "Residential",
  respite: "Respite",
  transportation: "Transportation",
  nursing: "Nursing",
  other: "Other",
}

/**
 * Rate unit options.
 */
export const RATE_UNITS = {
  per_hour: "per hour",
  per_day: "per day",
  per_session: "per session",
  per_month: "per month",
  flat: "flat rate",
}

/**
 * Generate HTML for a single location row.
 * @param {object} loc - location object { id, label, address, zip }
 * @returns {string} HTML string
 */
export function locationRowHtml(loc) {
  return `
    <div class="vendor-editor__loc-row" data-row="${esc(loc.id)}">
      <div class="vendor-editor__loc-fields">
        <input class="goal-editor__input vendor-editor__input--label" data-c="label" value="${esc(loc.label)}" placeholder="Label (e.g. Main Office)">
        <input class="goal-editor__input vendor-editor__input--address" data-c="address" value="${esc(loc.address)}" placeholder="Address">
        <input class="goal-editor__input vendor-editor__input--zip" data-c="zip" value="${esc(loc.zip)}" placeholder="ZIP">
      </div>
      <button type="button" class="goal-editor__del-btn" data-del="locations" title="Remove">×</button>
    </div>`
}

/**
 * Generate HTML for service type checkboxes (multi-select).
 * @param {Array<string>} selected - array of selected service type keys
 * @returns {string} HTML string
 */
export function serviceTypesHtml(selected) {
  const selectedSet = new Set(Array.isArray(selected) ? selected : [])
  const items = Object.entries(SERVICE_TYPES)
    .map(([k, label]) => {
      const checked = selectedSet.has(k) ? " checked" : ""
      return `<label class="vendor-editor__svc-item"><input type="checkbox" data-svc="${esc(k)}"${checked}><span>${esc(label)}</span></label>`
    })
    .join("")
  return `<div class="vendor-editor__svc-grid">${items}</div>`
}

/**
 * Generate HTML for rate unit select options.
 * @param {string} selected - selected unit key
 * @returns {string} HTML string
 */
export function rateUnitOptionsHtml(selected) {
  return Object.entries(RATE_UNITS)
    .map(([k, label]) => `<option value="${esc(k)}"${selected === k ? " selected" : ""}>${esc(label)}</option>`)
    .join("")
}

/**
 * Build the full-space vendor editor HTML (Settings → Vendors variant).
 *
 * @param {object} opts
 * @param {object} opts.vendor - vendor object { name, contactName, phone, email, website, serviceTypes, rate, locations, notes }
 * @param {string|null} opts.id - existing vendor id or null for new
 * @returns {string} full editor HTML string
 */
export function vendorEditorHtml({ vendor, id }) {
  const isNew = !id
  const heading = isNew ? "Add a vendor" : "Edit vendor"

  const v = vendor || {}
  const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes : []
  const rate = (v.rate && typeof v.rate === "object") ? v.rate : { amount: "", unit: "per_hour" }
  const locations = Array.isArray(v.locations) ? v.locations : []

  const locationsHtml = locations.length
    ? locations.map(locationRowHtml).join("")
    : `<div class="goal-editor__empty">No locations yet.</div>`

  return `
<div class="goal-editor vendor-editor">
  <div class="goal-editor__header">
    <h3 class="goal-editor__title">${heading}</h3>
    <p class="goal-editor__subtitle">Saved to your local vendor library.</p>
  </div>
  <div class="goal-editor__body">
    <div class="goal-editor__main">
      <label class="goal-editor__label">Vendor name</label>
      <input class="goal-editor__input" data-f="name" value="${esc(v.name)}">

      <label class="goal-editor__label">Contact name</label>
      <input class="goal-editor__input" data-f="contactName" value="${esc(v.contactName)}">

      <label class="goal-editor__label">Phone</label>
      <input class="goal-editor__input" data-f="phone" type="tel" value="${esc(v.phone)}">

      <label class="goal-editor__label">Email</label>
      <input class="goal-editor__input" data-f="email" type="email" value="${esc(v.email)}">

      <label class="goal-editor__label">Website</label>
      <input class="goal-editor__input" data-f="website" type="url" value="${esc(v.website)}" placeholder="https://...">

      <label class="goal-editor__label">Service types</label>
      ${serviceTypesHtml(serviceTypes)}

      <label class="goal-editor__label">Rate</label>
      <div class="vendor-editor__rate-row">
        <input class="goal-editor__input vendor-editor__input--rate-amt" data-f="rateAmount" type="text" inputmode="decimal" value="${esc(rate.amount)}" placeholder="Amount">
        <select class="goal-editor__input vendor-editor__input--rate-unit" data-f="rateUnit">${rateUnitOptionsHtml(rate.unit)}</select>
      </div>
    </div>
    <div class="goal-editor__aside">
      <label class="goal-editor__label">Locations</label>
      <div data-list="locations">${locationsHtml}</div>
      <button type="button" class="goal-editor__add-btn goal-editor__add-btn--vendor" data-add="locations">&#xFF0B; Add location</button>

      <label class="goal-editor__label vendor-editor__label--notes">Notes</label>
      <textarea class="goal-editor__input goal-editor__input--textarea" data-f="notes" rows="8">${esc(v.notes)}</textarea>
    </div>
  </div>
  <div class="goal-editor__actions">
    <button type="button" class="goal-editor__cancel-btn" data-role="cancel">Cancel</button>
    <button type="button" class="goal-editor__save-btn" data-role="save">${isNew ? "Add vendor" : "Save"}</button>
  </div>
</div>`
}

/**
 * Build a vendor list row for the Settings → Vendors list view.
 *
 * @param {object} vendor - vendor object
 * @param {function} escFn - escape function (usually esc)
 * @returns {string} HTML string
 */
export function vendorListRowHtml(vendor, escFn = esc) {
  const e = escFn
  const v = vendor || {}
  const serviceTypes = Array.isArray(v.serviceTypes) ? v.serviceTypes : []
  const locations = Array.isArray(v.locations) ? v.locations : []

  // Service type pills
  const pills = serviceTypes.slice(0, 4).map(st => {
    const label = SERVICE_TYPES[st] || st
    return `<span class="goal-editor__pill goal-editor__pill--purple">${e(label)}</span>`
  }).join("")
  const moreCount = serviceTypes.length > 4 ? serviceTypes.length - 4 : 0
  const morePill = moreCount > 0 ? `<span class="goal-editor__pill goal-editor__pill--blue">+${moreCount}</span>` : ""

  // Contact info
  const contactParts = []
  if (v.phone) contactParts.push(e(v.phone))
  if (v.email) contactParts.push(e(v.email))
  const contactStr = contactParts.join(" · ")

  // First location zip
  const firstZip = locations.length > 0 && locations[0].zip ? e(locations[0].zip) : ""

  return `
    <div class="goal-editor__list-row">
      <div class="goal-editor__list-row-header">
        <strong class="goal-editor__list-name">${e(v.name || "(unnamed)")}</strong>
        ${contactStr ? `<span class="goal-editor__list-meta">${contactStr}</span>` : ""}
        ${firstZip ? `<span class="goal-editor__list-meta">ZIP ${firstZip}</span>` : ""}
        <span class="goal-editor__list-actions">
          <button type="button" class="goal-editor__edit-btn" data-action="settings-vendors#edit" data-id="${e(v.id)}">Edit</button>
          <button type="button" class="goal-editor__delete-btn" data-action="settings-vendors#remove" data-id="${e(v.id)}">Delete</button>
        </span>
      </div>
      ${pills || morePill ? `<div class="goal-editor__list-chips">${pills}${morePill}</div>` : ""}
    </div>`
}
